import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import { TextField, Button, Container, Paper, Typography, Autocomplete, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { FieldArray, Form, Formik, FormikProps } from 'formik';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

import { useAppContext } from '../../../utils/content.util';
import { onError } from '../../../utils/error.util';
import { DealerDocument } from '../../../models/dealer-document.model';
import { createDealerDocument, getDealerDocument, updateDealerDocument } from '../../../services/dealer-documents.service';
import { CreateDealerDocument } from '../../../models/createDealerDocument.model';
import { dealerDocumentInitialValues, DealerDocumentSchemaYup, DOCUMENT_TYPES } from './validationSchema';
import { CircularProgressComponent } from '../../../components/admin/circular-progress.component';
import { isDev } from '../../../utils/utils';
import { getDealers } from '../../../services/dealers.service';
import { Dealer } from '../../../models/dealer.model';

export default function DealerDocumentFormContainer() {
  const { isAuthenticated } = useAppContext();

  const navigate = useNavigate();

  const { dealerDocumentId } = useParams();
  const [dealerDocument, setDealerDocument] = useState<DealerDocument>(dealerDocumentInitialValues);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [dealersLoading, setDealersLoading] = useState(false);

  useEffect(() => {
    async function loadDealers() {
      setDealersLoading(true);
      try {
        const response = await getDealers({ archived: false, limit: 1000 });
        setDealers(response.items);
      } catch (e) {
        onError(e);
      } finally {
        setDealersLoading(false);
      }
    }
    loadDealers();
  }, []);

  useEffect(() => {
    async function onLoad() {
      if (!isAuthenticated || !dealerDocumentId) {
        setIsLoading(false);
        return;
      }

      if (dealerDocumentId === 'new') {
        // new dealer document
        setIsLoading(false);
        return;
      }

      try {
        const response = await getDealerDocument(dealerDocumentId);

        setDealerDocument({ ...response, date: new Date(response.date) });
      } catch (e) {
        onError(e);
      }

      setIsLoading(false);
    }

    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerDocumentId]);

  const onSubmit = async (values: any) => {
    setIsSubmitting(true);
    try {
      if (dealerDocument.id) {
        const payload: DealerDocument = {
          ...dealerDocument,
          ...values,
          requestedDocuments: values.requestedDocuments.map((doc: any) => ({
            documentType: doc.documentType,
            description: doc.description || undefined,
            fileUrl: doc.fileUrl || undefined,
            fileName: doc.fileName || undefined,
          })),
        };

        await updateDealerDocument(payload);
      } else {
        const { id, ...createDealerDocumentData } = values;
        const selectedDealer = dealers.find((d) => d.companyName === values.dealerName);
        const payload: CreateDealerDocument = {
          ...createDealerDocumentData,
          dealerId: selectedDealer?.id,
          requestedDocuments: values.requestedDocuments.map((doc: any) => ({
            documentType: doc.documentType,
            description: doc.description || undefined,
            fileUrl: doc.fileUrl || undefined,
            fileName: doc.fileName || undefined,
          })),
        };

        await createDealerDocument(payload);
      }
      setIsSubmitting(false);
      navigate('/dealer-documents');
    } catch (err) {
      onError(err);
      alert('Something wrong');
    } finally {
      setIsSubmitting(false);
    }
  };

  function renderDealerDocumentItem() {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Formik
          initialValues={dealerDocument}
          validationSchema={DealerDocumentSchemaYup}
          onSubmit={(values) => onSubmit(values)}
          enableReinitialize
        >
          {({ handleChange, handleBlur, values, touched, errors, setFieldValue }: FormikProps<any>) => (
            <Form noValidate>
              <fieldset disabled={dealerDocument.status === 'done'}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <Autocomplete
                      options={dealers}
                      getOptionLabel={(option) => (typeof option === 'string' ? option : option.companyName)}
                      value={dealers.find((d) => d.companyName === values.dealerName) || null}
                      onChange={(event, newValue) => {
                        setFieldValue('dealerName', newValue ? newValue.companyName : '');
                        setFieldValue('dealerId', newValue ? newValue.id : '');
                      }}
                      loading={dealersLoading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Dealer Name"
                          error={touched.dealerName && Boolean(errors.dealerName)}
                          helperText={touched.dealerName && String(errors.dealerName || '')}
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 6 }}>
                    <TextField
                      name="firstName"
                      label="First Name"
                      variant="outlined"
                      fullWidth
                      value={values.firstName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.firstName && Boolean(errors.firstName)}
                      helperText={touched.firstName && String(errors.firstName || '')}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      name="lastName"
                      label="Last Name"
                      variant="outlined"
                      fullWidth
                      value={values.lastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.lastName && Boolean(errors.lastName)}
                      helperText={touched.lastName && String(errors.lastName || '')}
                    />
                  </Grid>

                  <Grid size={{ xs: 6 }}>
                    <TextField
                      name="email"
                      label="Email"
                      variant="outlined"
                      type="email"
                      fullWidth
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && String(errors.email || '')}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      name="phone"
                      label="Phone"
                      variant="outlined"
                      fullWidth
                      value={values.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.phone && Boolean(errors.phone)}
                      helperText={touched.phone && String(errors.phone || '')}
                      slotProps={{
                        input: {
                          inputMode: 'numeric',
                          type: 'tel',
                          placeholder: '(XXX) XXX-XXXX',
                        },
                      }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <TextField
                      name="description"
                      label="Description"
                      variant="outlined"
                      fullWidth
                      multiline
                      rows={4}
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.description && Boolean(errors.description)}
                      helperText={touched.description && String(errors.description || '')}
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <DatePicker
                      label="Date"
                      value={values.date ? dayjs(values.date) : null}
                      onChange={(newValue) => setFieldValue('date', newValue?.toDate())}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: touched.date && Boolean(errors.date),
                          helperText: touched.date && String(errors.date),
                        },
                      }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Paper
                      elevation={0}
                      sx={{
                        // border: '1px solid #e0e0e0',
                        // borderRadius: 1,
                        // p: 2,
                        // mb: 2,
                      }}
                    >
                      <Typography variant="h5" sx={{ mb: 2 }}>
                        Requested Documents
                      </Typography>

                      <FieldArray
                        name="requestedDocuments"
                        render={(arrayHelpers) => (
                          <div>
                            {(values.requestedDocuments || []).map((doc: any, index: number) => (
                              <div key={index}>
                                <Grid
                                  container
                                  spacing={2}
                                  sx={{
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1,
                                    p: 2,
                                    mb: 2,
                                  }}
                                >
                                  <Grid size={{ xs: 12 }}>
                                    <FormControl fullWidth>
                                      <InputLabel id={`document-type-label-${index}`}>Document Type</InputLabel>
                                      <Select
                                        labelId={`document-type-label-${index}`}
                                        id={`document-type-${index}`}
                                        value={doc.documentType || ''}
                                        onChange={(e) => setFieldValue(`requestedDocuments[${index}].documentType`, e.target.value)}
                                        label="Document Type"
                                      >
                                        {DOCUMENT_TYPES.map((type) => (
                                          <MenuItem key={type} value={type}>
                                            {type}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Grid>

                                  <Grid size={{ xs: 12 }}>
                                    <TextField
                                      name={`requestedDocuments[${index}].description`}
                                      label="Description (Optional)"
                                      variant="outlined"
                                      multiline
                                      rows={3}
                                      fullWidth
                                      value={doc.description || ''}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                    />
                                  </Grid>

                                  {values.requestedDocuments.length > 1 && (
                                    <Grid size={{ xs: 12 }}>
                                      <Button type="button" onClick={() => arrayHelpers.remove(index)}>
                                        Remove
                                      </Button>
                                    </Grid>
                                  )}
                                </Grid>
                                {index === values.requestedDocuments.length - 1 && (
                                  <Button
                                    type="button"
                                    onClick={() => arrayHelpers.push({ documentType: '', description: '' })}
                                  >
                                    Add Document
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      />
                    </Paper>
                  </Grid>
                </Grid>
              </fieldset>
              <Grid size={{ xs: 12 }} sx={{ display: 'flex', gap: 1, my: 2 }}>
                <Button variant="contained" type="submit" disabled={isSubmitting} hidden={dealerDocument.status === 'done'}>
                  Save
                </Button>
                <Button variant="outlined" onClick={() => navigate(-1)}>
                  Cancel
                </Button>
              </Grid>

              {isDev ? (
                <>
                  <div>
                    <p>Values:</p>
                    <code>{JSON.stringify(values, null, 2)}</code>
                  </div>
                  <div>
                    <p>Errors:</p>
                    <code>{JSON.stringify(errors, null, 2)}</code>
                  </div>
                </>
              ) : null}
            </Form>
          )}
        </Formik>
      </Container>
    );
  }

  return (
    <>
      <div className="dealer-document-items">
        <h2 className="pb-2 mt-4 mb-3 border-bottom">
          {dealerDocumentId === 'new' ? 'Add New Dealer Document' : 'Edit Dealer Document'}
        </h2>
      </div>
      {isLoading ? <CircularProgressComponent /> : renderDealerDocumentItem()}
    </>
  );
}

